// Types
import { Selection } from '@tiptap/pm/state';
import { Node as ProseMirrorNode, ResolvedPos } from '@tiptap/pm/model';

export type SelectionAncestorNodeContext = {
    /** The matched ancestor node's type name. */
    nodeType: string;
    /** ProseMirror position immediately before the matched ancestor node. */
    position: number;
    node: ProseMirrorNode;
};

/**
 * Walks up from a resolved position to the nearest ancestor whose type is in `nodeTypes`.
 */
const getAncestorNodeContextAtPosition = (
    $position: ResolvedPos,
    nodeTypes: Set<string>,
): SelectionAncestorNodeContext | undefined => {
    for (let depth = $position.depth; depth >= 0; depth--) {
        const node = $position.node(depth);
        const nodeType = node.type.name;
        if (nodeTypes.has(nodeType)) {
            return {
                nodeType,
                position: $position.before(depth),
                node,
            };
        }
    }

    return undefined;
};

/**
 * Returns the single matching ancestor node the whole selection sits within, or
 * `undefined` when the selection's endpoints resolve to different matching nodes
 * (e.g. spanning two task items, or a mixed title/task selection). The returned
 * `position` targets the exact node instance so nested nodes stay unambiguous.
 */
const getSelectionAncestorNodeContext = (
    selection: Selection,
    nodeTypes: Set<string>,
): SelectionAncestorNodeContext | undefined => {
    const fromContext = getAncestorNodeContextAtPosition(selection.$from, nodeTypes);
    const toContext = getAncestorNodeContextAtPosition(selection.$to, nodeTypes);

    if (!fromContext || !toContext) return undefined;
    if (fromContext.nodeType !== toContext.nodeType) return undefined;
    if (fromContext.position !== toContext.position) return undefined;

    return fromContext;
};

export default getSelectionAncestorNodeContext;
